/**
 * PersonDetailDrawer: a roster row's own detail drawer.
 *
 * Reuses the People page's own drawer chrome (PersonDrawer) and its
 * Directory-fields editor (DirectoryFieldsEditor) verbatim -- this is the
 * same drawer the Admin People page uses, not a second one built to look
 * like it. What differs is the photo (TeamPhoto, at the directory's own
 * lower capability floor, never CmPhoto) and who gets to write: a caller
 * holding manage_staff_profiles (the same floor the People page's own
 * editor gates on) sees the editable birthday/anniversary/start-date/
 * private-contact fields; everyone else sees the same facts read-only.
 * Role, unit, work phone and work email stay read-only here regardless --
 * changing a person's designation or unit is an Admin People action, not a
 * roster one.
 */

import { SignatureBlockField } from '@/components/SignatureBlockField';
import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DirectoryFieldsEditor,
  PersonDrawer,
  homeSaveOutcome,
  homeAddressLine,
  homePointLabel,
  homePointSavedMessage,
  type DirectoryFieldValues,
} from '@/components/PeopleTab';
import { DrawerRow, DrawerRowList, DrawerSection } from '@/components/workspace/drawer/drawerLayout';
import { Button } from '@/components/primitives/Button';
import { staffAdminApi, type StaffDirectoryFieldsPatch } from '@/lib/staffAdmin';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  anniversaryLine,
  birthdayLabel,
  contactCardText,
  roleLine,
  teamDirectoryQueryKey,
  type TeamDirectoryPerson,
} from '@/lib/teamDirectory';
import { formatPhoneDisplay } from '@/lib/phone';
import { TeamPhoto } from './TeamPhoto';

const CHIP = 'inline-flex items-center min-h-5 px-2 py-0.5 text-label font-medium bg-sand text-ink-soft border border-sage-800/[8%] rounded-full';

function directoryFieldValues(person: TeamDirectoryPerson): DirectoryFieldValues {
  return {
    birthday_month: person.birthday_month,
    birthday_day: person.birthday_day,
    work_anniversary: person.work_anniversary,
    start_date: person.start_date,
    home_address: person.home_address,
    home_address_line1: person.home_address_line1 ?? null,
    home_address_line2: person.home_address_line2 ?? null,
    home_address_city: person.home_address_city ?? null,
    home_address_state: person.home_address_state ?? null,
    home_address_zip: person.home_address_zip ?? null,
    home_city: person.home_city ?? null,
    home_zip: person.home_zip ?? null,
    personal_phone: person.personal_phone,
  };
}

export interface PersonDetailDrawerProps {
  tenantSlug: string;
  person: TeamDirectoryPerson;
  today: string;
  /** Whether the SERVER served the private pair to this caller (the
   *  response's own private_contact_visible) -- gates both reading it here
   *  and whether the editor offers the two fields to write. */
  privateContactVisible: boolean;
  /** manage_staff_profiles: the same floor the People page's editor gates on. */
  canEdit: boolean;
  onClose: () => void;
}

export function PersonDetailDrawer({
  tenantSlug,
  person,
  today,
  privateContactVisible,
  canEdit,
  onClose,
}: PersonDetailDrawerProps): ReactNode {
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const directoryMutation = useMutation({
    mutationFn: (v: { rosterId: string; patch: StaffDirectoryFieldsPatch }) =>
      staffAdminApi.setDirectoryFields(tenantSlug, v.rosterId, v.patch),
    onSuccess: (result) => {
      // The save landed; the hint slot only carries the map outcome when
      // the address could not be placed (a found address needs no note).
      setError(homePointSavedMessage(result.home_point));
      void queryClient.invalidateQueries({ queryKey: teamDirectoryQueryKey(tenantSlug) });
    },
    onError: (e: unknown) => setError(pageWriteText(e, contact)),
  });

  async function copyContact(): Promise<void> {
    try {
      await navigator.clipboard.writeText(contactCardText(person));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const birthday = birthdayLabel(person.birthday_month, person.birthday_day);
  const anniversary = anniversaryLine(person, today);
  // The private block renders when anything in it is on file, the map's
  // home point included: a care manager the map already places, with no
  // street on file, shows that fact instead of a bare "Not on file".
  const hasPrivate =
    person.home_address !== null ||
    person.personal_phone !== null ||
    homePointLabel(person.home_city, person.home_zip) !== null;

  return (
    <PersonDrawer
      name={person.full_name}
      photo={<TeamPhoto tenantSlug={tenantSlug} person={person} size="lg" />}
      onClose={onClose}
      badges={
        <>
          <span className={CHIP} data-testid="person-drawer-role">{roleLine(person)}</span>
          {person.unit_name && <span className={CHIP} data-testid="person-drawer-unit">{person.unit_name}</span>}
        </>
      }
    >
      <DrawerSection title="Contact" testId="person-drawer-contact">
        <DrawerRowList>
          <DrawerRow label="Work phone" testId="person-drawer-work-phone">
            {person.work_phone ? formatPhoneDisplay(person.work_phone) : 'Not on file'}
          </DrawerRow>
          <DrawerRow label="Work email" testId="person-drawer-work-email">
            {person.work_email ? (
              <a href={`mailto:${person.work_email}`} className="text-sage-800 underline-offset-2 hover:underline">
                {person.work_email}
              </a>
            ) : (
              'Not on file'
            )}
          </DrawerRow>
        </DrawerRowList>
      </DrawerSection>

      {/* 0239: the lines printed under this person's name on the documents
          they sign. The person edits their own; the office edits anyone's. */}
      <DrawerSection title="Signature" testId="person-drawer-signature">
        {canEdit || person.is_you === true ? (
          <SignatureBlockField
            tenantSlug={tenantSlug}
            rosterId={person.roster_id}
            name={person.full_name}
            value={person.signature_block ?? null}
            testId="person-drawer-signature-block"
          />
        ) : (
          <DrawerRowList>
            <DrawerRow label="Signature block">
              <span className="whitespace-pre-line" data-testid="person-drawer-signature-block-text">
                {person.signature_block ? person.signature_block : 'Not on file'}
              </span>
            </DrawerRow>
          </DrawerRowList>
        )}
      </DrawerSection>

      {canEdit ? (
        <DrawerSection title="Directory details" testId="person-drawer-directory" hint={error ?? undefined}>
          <DirectoryFieldsEditor
            rosterId={person.roster_id}
            name={person.full_name}
            fields={directoryFieldValues(person)}
            privateContactVisible={privateContactVisible}
            busy={directoryMutation.isPending}
            onSave={(patch) => directoryMutation.mutate({ rosterId: person.roster_id, patch })}
            onSaveHome={async (parts) =>
              homeSaveOutcome(
                await directoryMutation.mutateAsync({ rosterId: person.roster_id, patch: { home_address: parts } }),
              )
            }
          />
        </DrawerSection>
      ) : (
        <DrawerSection title="Directory details" testId="person-drawer-directory">
          <DrawerRowList>
            <DrawerRow label="Birthday" testId="person-drawer-birthday">{birthday ?? 'Not on file'}</DrawerRow>
            <DrawerRow label="Since" testId="person-drawer-since">{anniversary ?? 'Not on file'}</DrawerRow>
            {hasPrivate && (
              <>
                <DrawerRow label="Home address" testId="person-drawer-home-address">
                  {homeAddressLine(person.home_address, person.home_city ?? null, person.home_zip ?? null)}
                </DrawerRow>
                <DrawerRow label="Personal phone" testId="person-drawer-personal-phone">
                  {person.personal_phone ? formatPhoneDisplay(person.personal_phone) : 'Not on file'}
                </DrawerRow>
              </>
            )}
          </DrawerRowList>
        </DrawerSection>
      )}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={copied ? <Check size={14} /> : <Copy size={14} />}
        onClick={() => void copyContact()}
        data-testid="person-drawer-copy-contact"
        aria-label={`Copy contact for ${person.full_name}`}
      >
        {copied ? 'Copied' : 'Copy contact'}
      </Button>
    </PersonDrawer>
  );
}
